import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * New Jersey Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const NJLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: ['24 LU biennial', 'Min 12 LU in HSW', '1 LU Barrier-Free Design', '1 LU Sustainability'],
      stateBoard: 'NJ Division of Consumer Affairs - Board of Architects',
      boardUrl: 'https://www.njconsumeraffairs.gov/arch'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 24,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['24 PDH biennial', 'Min 2 PDH in Ethics (max 8 PDH)', 'Technical competence subjects'],
      stateBoard: 'NJ Division of Consumer Affairs - Board of Professional Engineers',
      boardUrl: 'https://www.njconsumeraffairs.gov/pels'
    },
    {
      id: 'PLS',
      name: 'Professional Land Surveyor',
      icon: '📐',
      credits: 24,
      cycleYears: 2,
      terminology: 'CPC',
      highlights: ['24 hours CPC biennial', 'Min 1 hour Ethics (max 8 hours)', 'Professional competency activities'],
      stateBoard: 'NJ Division of Consumer Affairs - Board of Professional Engineers & Land Surveyors',
      boardUrl: 'https://www.njconsumeraffairs.gov/pels'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🤝',
      credits: 40,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['40 hours biennial', 'Min 20 hours Clinical Practice', 'Max 20 hours non-clinical/elective'],
      stateBoard: 'NJ Division of Consumer Affairs - Social Work Board',
      boardUrl: 'https://www.njconsumeraffairs.gov/swb'
    },
    {
      id: 'LSW',
      name: 'Licensed Social Worker',
      icon: '👥',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', 'General social work practice', 'Variety of approved CE activities'],
      stateBoard: 'NJ Division of Consumer Affairs - Social Work Board',
      boardUrl: 'https://www.njconsumeraffairs.gov/swb'
    },
    {
      id: 'CSW',
      name: 'Certified Social Worker',
      icon: '🎓',
      credits: 20,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['20 hours biennial', 'Social work knowledge and skills', 'Approved providers accepted'],
      stateBoard: 'NJ Division of Consumer Affairs - Social Work Board',
      boardUrl: 'https://www.njconsumeraffairs.gov/swb'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', 'Nursing practice and patient care', 'No specific mandatory topics'],
      stateBoard: 'NJ Division of Consumer Affairs - Board of Nursing',
      boardUrl: 'https://www.njconsumeraffairs.gov/nur'
    },
    {
      id: 'LPN',
      name: 'Licensed Practical Nurse',
      icon: '💉',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', 'Practical nursing practice', 'No specific mandatory topics'],
      stateBoard: 'NJ Division of Consumer Affairs - Board of Nursing',
      boardUrl: 'https://www.njconsumeraffairs.gov/nur'
    },
    {
      id: 'APN',
      name: 'Advanced Practice Nurse',
      icon: '🩺',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', 'Advanced practice specialty', 'Evidence-based practice'],
      stateBoard: 'NJ Division of Consumer Affairs - Board of Nursing',
      boardUrl: 'https://www.njconsumeraffairs.gov/nur'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 120,
      cycleYears: 3,
      terminology: 'CPE',
      highlights: ['120 hours triennial (Jan 1 - Dec 31)', 'Min 60 hours Technical', 'Min 24 hours Accounting or Auditing', 'Min 4 hours NJ Law & Ethics'],
      stateBoard: 'NJ Division of Consumer Affairs - Board of Accountancy',
      boardUrl: 'https://www.njconsumeraffairs.gov/acc'
    },
    {
      id: 'COUNS',
      name: 'Professional Counselor',
      icon: '🧠',
      credits: 40,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['40 hours biennial', 'Counseling practice and ethics', 'Clinical competency activities'],
      stateBoard: 'NJ Division of Consumer Affairs - Professional Counselor Board',
      boardUrl: 'https://www.njconsumeraffairs.gov/pce'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Salesperson',
      icon: '🏠',
      credits: 12,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['12 hours biennial', 'Real estate practice and law', 'Ethics and professional conduct'],
      stateBoard: 'NJ Real Estate Commission',
      boardUrl: 'https://www.njconsumeraffairs.gov/rec'
    }
  ];

  const structuredData = [
    // Main WebPage schema
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "New Jersey Professional License Renewal Requirements",
      "description": "Complete guide to New Jersey professional license renewal requirements for Architects, Engineers, Nurses, CPAs, Social Workers, and more.",
      "url": "https://cectrack.com/nj-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.njconsumeraffairs.gov"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "New Jersey Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "New Jersey License Renewal",
            "item": "https://cectrack.com/nj-license-renewal"
          }
        ]
      }
    },
    // FAQPage schema
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my New Jersey professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in New Jersey. Architects and Engineers need 24 PDH/LU every 2 years, CPAs need 40-120 hours every 3 years (depending on practice area), Nurses need 30 hours every 2 years, and Social Workers need 40 hours every 2 years."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in New Jersey?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "New Jersey professional licenses are overseen by the NJ Division of Consumer Affairs, which includes boards for Architects, Engineers, CPAs, Nurses, Social Workers, and other professions."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my New Jersey professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most New Jersey professions renew biennially (every 2 years), including Architects, Engineers, Nurses, and Social Workers. CPAs renew every 3 years."
          }
        },
        {
          "@type": "Question",
          "name": "Does New Jersey accept online CE courses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, New Jersey accepts online continuing education courses from approved providers. However, some professions have limits on self-study or online hours. Check with your specific board for approved providers and requirements."
          }
        }
      ]
    },
    // GovernmentOrganization schema
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "New Jersey Division of Consumer Affairs",
      "url": "https://www.njconsumeraffairs.gov",
      "areaServed": "New Jersey",
      "description": "Licenses and regulates professional boards including Architects, Engineers, CPAs, Nurses, and Social Workers in New Jersey"
    }
  ];

  return (
    <>
      <SEOHelmet
        title="New Jersey Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to New Jersey professional license renewal requirements. Track CE credits for Architects, Engineers, Nurses, CPAs, Social Workers, and more."
        keywords="New Jersey license renewal, NJ CE requirements, NJ continuing education, New Jersey professional licenses, NJ Division of Consumer Affairs, NJ nurses, NJ CPAs, NJ engineers"
        canonical="https://cectrack.com/nj-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        style={{
          background: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                New Jersey Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Streamline your New Jersey CE tracking. Manage all your professional license requirements in one secure platform.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-4">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#4c1d95', marginBottom: '0.5rem' }}>12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#4c1d95', marginBottom: '0.5rem' }}>100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>NJ Compliant</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#4c1d95', marginBottom: '0.5rem' }}>24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Secure Cloud Access</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <section id="professions" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              New Jersey CE Requirements by Profession
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Comprehensive continuing education requirements for all NJ licensed professionals
            </p>
          </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#ede9fe',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#4c1d95' }}>
                        {prof.credits} {prof.terminology}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 2 ? 'Biennial' : prof.cycleYears === 3 ? 'Triennial' : 'Annual'} Renewal
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#7c3aed', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify New Jersey License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                New Jersey professionals trust CEC Track for automated tracking, secure document storage, 
                and comprehensive compliance reporting for all Division of Consumer Affairs requirements.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Track multiple NJ licenses in one dashboard',
                  'Automatic deadline calculations and email reminders',
                  'Upload and categorize CE certificates',
                  'Real-time progress tracking by category',
                  'Secure cloud storage for audit documentation',
                  'Multi-state support for cross-border professionals'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎯</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join New Jersey professionals who never worry about missing CE requirements.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              New Jersey State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources for New Jersey professional licensing
            </p>
          </div>

          <div className="row g-4 justify-content-center">
            <div className="col-md-8">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    NJ Division of Consumer Affairs
                  </h4>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    The Division of Consumer Affairs regulates over 50 professional and occupational boards 
                    in New Jersey, including architects, engineers, nurses, social workers, CPAs, real estate professionals, 
                    and healthcare practitioners.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>General Information:</strong> (973) 504-6200
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.njconsumeraffairs.gov" target="_blank" rel="noopener noreferrer">
                      www.njconsumeraffairs.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Office:</strong> 124 Halsey Street, Newark, NJ 07102
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Mailing:</strong> P.O. Box 45003, Newark, NJ 07101
                  </div>
                  <a 
                    href="https://www.njconsumeraffairs.gov/pages/boards.aspx" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary me-2"
                  >
                    View All NJ Boards
                  </a>
                  <a 
                    href="https://newjersey.mylicense.com/verification/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-secondary"
                  >
                    Verify a License
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mt-3">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3">
                  <h6 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Board of Architects</h6>
                  <a href="https://www.njconsumeraffairs.gov/arch" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                    Visit Board →
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3">
                  <h6 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Board of Professional Engineers & Land Surveyors</h6>
                  <a href="https://www.njconsumeraffairs.gov/pels" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                    Visit Board →
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3">
                  <h6 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Board of Nursing</h6>
                  <a href="https://www.njconsumeraffairs.gov/nur" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                    Visit Board →
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3">
                  <h6 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Social Work Board</h6>
                  <a href="https://www.njconsumeraffairs.gov/swb" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                    Visit Board →
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3">
                  <h6 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Board of Accountancy</h6>
                  <a href="https://www.njconsumeraffairs.gov/acc" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                    Visit Board →
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3">
                  <h6 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Real Estate Commission</h6>
                  <a href="https://www.njconsumeraffairs.gov/rec" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                    Visit Board →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="NJ" />

      {/* CTA Section */}
      <section 
        style={{
          background: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your New Jersey License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join New Jersey professionals who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
    </>
  );
};

export default NJLicenseRenewal;
